import { TreeViewResponseItem } from "@/apis/preview/v2/types";
import { getTagSearchResultsV2 } from "@/apis/taxonomy/route";
import {
  TagSearchResultsItemV2,
  TagSearchResultsResponseV2,
} from "@/apis/taxonomy/types";
import Input from "@/components/common/input";
import { SearchIcon } from "@/components/icons/search";
import { ModalWrapper } from "@/components/modals/modal-wrapper";
import { cn } from "@/lib/utils";
import { popModal } from "@/stores/modal";
import { TagType } from "@/types/mapping";
import { useMutation } from "@tanstack/react-query";
import { ArrowLeft, ChevronRight, Loader2, Plus, X } from "lucide-react";
import { useState } from "react";
import { DimensionIcon } from "./new-tree-view/tree-tag-item";

interface AddTagModalProps {
  /** 모달은 ModalStack(앱 루트)에서 렌더되어 preview store 바깥이므로 push 시점에 받는다. */
  workbookId: string | null;
  tableId: string | null;
  roleKey: string;
  dimension: "LINE_ITEM" | "AXIS";
  treeItems: TreeViewResponseItem[];
  preSelectedParent?: { tagKey: string | null; label: string } | null;
  onTagSelected: (
    tag: TagSearchResultsItemV2,
    parentTagKey: string | null,
  ) => void;
}

type Step = "select-parent" | "search-tag";

export const AddTagModal: React.FC<AddTagModalProps> = ({
  workbookId,
  tableId,
  roleKey,
  dimension,
  treeItems,
  preSelectedParent,
  onTagSelected,
}) => {
  const [step, setStep] = useState<Step>(
    preSelectedParent ? "search-tag" : "select-parent",
  );
  const [selectedParent, setSelectedParent] = useState<{
    tagKey: string | null;
    label: string;
  } | null>(preSelectedParent ?? null);
  const [query, setQuery] = useState("");
  const [addingTagKey, setAddingTagKey] = useState<string | null>(null);

  const {
    mutate: search,
    isPending,
    data,
  } = useMutation<TagSearchResultsResponseV2, Error, void>({
    mutationFn: () => {
      return getTagSearchResultsV2(workbookId!, query, tableId!, dimension);
    },
  });

  const handleSearch = () => {
    if (!workbookId || !query.trim()) return;
    search();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.nativeEvent.isComposing) {
      handleSearch();
    }
  };

  const handleSelectParent = (tagKey: string | null, label: string) => {
    setSelectedParent({ tagKey, label });
    setStep("search-tag");
  };

  const handleSelectTag = (tag: TagSearchResultsItemV2) => {
    setAddingTagKey(tag.key);
    onTagSelected(tag, selectedParent?.tagKey ?? null);
    popModal();
  };

  const handleBackToParent = () => {
    setStep("select-parent");
    setQuery("");
    setSelectedParent(null);
  };

  // Filter tree items to show only valid parent candidates
  // (ABSTRACT, LINE_ITEMS, AXIS, TABLE types can be parents)
  const parentCandidates = treeItems.filter(
    (item) =>
      item.tagType === "ABSTRACT" ||
      item.tagType === "LINE_ITEMS" ||
      item.tagType === "AXIS" ||
      item.tagType === "TABLE",
  );

  const totalResults =
    data?.tags?.reduce((acc, group) => acc + group.items.length, 0) ?? 0;

  return (
    <ModalWrapper className="bg-white rounded-[10px] w-[480px] max-h-[600px] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-neutral-200">
        <div className="flex items-center gap-2">
          {step === "search-tag" && !preSelectedParent && (
            <button
              type="button"
              title="뒤로"
              onClick={handleBackToParent}
              className="p-1 rounded hover:bg-neutral-100"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}
          <span className="headline-2-semibold">
            {step === "select-parent" ? "부모 태그 선택" : "태그 검색"}
          </span>
        </div>
        <button
          type="button"
          title="닫기"
          onClick={() => popModal()}
          className="p-1 rounded hover:bg-neutral-100"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Step 1: Select Parent */}
      {step === "select-parent" && (
        <div className="flex-1 overflow-y-auto">
          <div className="p-4 pb-2">
            <p className="caption-2-medium text-neutral-500">
              추가할 태그의 위치를 선택하세요
            </p>
          </div>

          <div className="px-4 pb-2">
            <button
              type="button"
              onClick={() => handleSelectParent(null, "최상위")}
              className="flex items-center gap-2 p-3 rounded-md text-left w-full hover:bg-blue-50 transition-colors border border-dashed border-neutral-300"
            >
              <Plus className="w-4 h-4 text-blue-500" />
              <span className="label-1-normal-medium text-blue-600">
                최상위에 추가
              </span>
            </button>
          </div>

          <div className="flex flex-col gap-0.5 px-4 pb-4">
            {parentCandidates.map((item) => (
              <button
                type="button"
                key={`${item.tagKey}-${item.depth}`}
                onClick={() => handleSelectParent(item.tagKey, item.tagKoLabel)}
                className="flex items-center gap-2 p-2 rounded-md text-left w-full hover:bg-blue-50 transition-colors group"
              >
                {item.depth > 0 && (
                  <div
                    className={cn(
                      "flex-shrink-0",
                      item.depth === 1 && "w-0",
                      item.depth === 2 && "w-4",
                      item.depth === 3 && "w-8",
                      item.depth === 4 && "w-12",
                      item.depth >= 5 && "w-16",
                    )}
                  />
                )}
                <DimensionIcon
                  tagType={item.tagType as TagType}
                  isSum={item.isSum}
                  isExtendedTag={item.isExtendedTag}
                />
                <div className="flex flex-col min-w-0 flex-1">
                  <span className="label-1-normal-medium truncate">
                    {item.tagKoLabel}
                  </span>
                  <span className="caption-2-medium text-neutral-400 truncate">
                    {item.tagKey}
                  </span>
                </div>
                <ChevronRight className="w-4 h-4 text-neutral-300 group-hover:text-blue-500 flex-shrink-0" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: Search & Select Tag */}
      {step === "search-tag" && (
        <>
          <div className="p-4 pb-2">
            {selectedParent && (
              <div className="flex items-center gap-1.5 mb-3 px-3 py-2 bg-blue-50 rounded-md">
                <span className="caption-2-medium text-neutral-500">
                  추가 위치:
                </span>
                <span className="caption-2-semibold text-blue-700">
                  {selectedParent.label}
                </span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Input
                placeholder="태그 이름 검색"
                size="large"
                className="rounded-[8px] border border-[#E5E5E5] shadow-sm"
                icon={<SearchIcon />}
                onKeyDown={handleKeyDown}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            {totalResults > 0 && (
              <div className="mt-2 caption-1-semibold text-blue-500">
                총 {totalResults}개의 검색 결과
              </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto px-4 pb-4">
            {isPending ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
              </div>
            ) : data?.tags && data.tags.length > 0 ? (
              <div className="flex flex-col gap-1">
                {data.tags.flatMap((group) =>
                  group.items.map((tag) => (
                    <button
                      type="button"
                      key={tag.key}
                      onClick={() => handleSelectTag(tag)}
                      disabled={addingTagKey === tag.key}
                      className={cn(
                        "flex items-center gap-2 p-2 rounded-md text-left w-full",
                        "hover:bg-blue-50 transition-colors",
                        addingTagKey === tag.key && "opacity-50",
                      )}
                    >
                      <DimensionIcon
                        tagType={tag.type as TagType}
                        isSum={tag.isSum}
                        isExtendedTag={false}
                      />
                      <div className="flex flex-col min-w-0 flex-1">
                        <span className="label-1-normal-medium truncate">
                          {tag.koLabel}
                        </span>
                        <span className="caption-2-medium text-neutral-400 truncate">
                          {tag.key}
                        </span>
                      </div>
                      <Plus className="w-4 h-4 text-blue-500 flex-shrink-0" />
                    </button>
                  )),
                )}
              </div>
            ) : data?.tags && data.tags.length === 0 ? (
              <div className="flex items-center justify-center py-12 text-neutral-400 label-1-normal-medium">
                검색 결과가 없습니다
              </div>
            ) : null}
          </div>
        </>
      )}
    </ModalWrapper>
  );
};
